import React, {useEffect, useState, memo} from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListSubheader,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useTheme
} from '@material-ui/core';
import clsx from 'clsx';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import {
  AerialAreaMeasurement, AerialLengthMeasurement,
  HeightMeasurement,
  Measurement3D, MeasurementArea, MeasurementPoint,
  MeasurementVolume, StationMeasurement
} from '../viewer/js/measurements';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import 'font-awesome/css/font-awesome.css';
import { dialog, fs, isStaticSite } from "../electron-modules";
import slash from 'slash';
import fileDialog from 'file-dialog';
import { useSelector, useDispatch } from 'react-redux';
import { selectMeasureUnits, changeMeasureUnits, selectDataProjection } from '../redux/projections-slice';
import LocalScene from '../viewer/js/projections';
import {
  ArrowTooltip,
  PromptDialog,
  TextDialog,
  DenseDivider,
  IconToolBar,
  SlimScrollbar,
  ToolBarButton,
  ToolBarDivider,
  DraggableDialog,
  DenseMenu
} from '../components';
import { toast } from '../app';
import { measureDataFilter, measureDataFormat } from '../file-extensions';
import { useTranslation } from 'react-i18next';
import {selectAllAssets, getLandXMLAssets, XMLData} from "../redux/assets-slice";
import {
  useDialog,
  useUniqueProjectID,
  useViewer,
  useActiveTool,
  useContextMenu
} from '../hooks';
import { nanoid } from '@reduxjs/toolkit';
import { VolumeType, SamplingRate, MeasureType } from '../types/measurements';

const copyTextToClipboard = require('copy-to-clipboard');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    copyClipboard: {
      cursor: "pointer"
    },
    textField: {
      marginBottom: theme.spacing(3)
    },
    userWarning: {
      display: "block",
      padding: theme.spacing(1, 0),
      color: theme.palette.warning.main,
      textAlign: "center"
    },
    cardButtons: {
      marginTop: 0,
      alignSelf: "center"
    },
    card: {
      margin: theme.spacing(2),
      marginTop: theme.spacing(0),
      padding: theme.spacing(1),
      userSelect: "none"
    },
    cardHeader: {
      padding: theme.spacing(1)
    },
    cardContent: {
      padding: `${theme.spacing(2)}px !important`
    },
    footerDiv: {
      width: "100%",
      boxShadow: "0px 10px 10px 2px",
      zIndex: 1,
      textAlign: "center"
    },
    measurementUnitsParent: {
      justifyContent: "space-between",
      padding: theme.spacing(0, 2),
      flexWrap: "nowrap"
    },
    measurementUnits: {
      margin: theme.spacing(0)
    },
    measurementUnitsRadio: {
      padding: theme.spacing(0.5),
      margin: theme.spacing(0)
    },
    imageOnlyInfoButton: {
      marginLeft: theme.spacing(1)
    },
    marginside: {
      margin: 0,
      padding: "10px"
    },
    actions: {
      padding: "5px"
    },
    expandText: {
      width: "100%",
      display: "block",
      textAlign: "center",
      cursor: "pointer",
      userSelect: "none",
      paddingBottom: "5px",
      paddingTop: "5px"
    },
    bold: {
      fontWeight: "bold"
    },
    row: {
      display: "flex",
      width: "100%",
      alignItems: "baseline"
    },
    alignmentSelect: {
      alignSelf: "center",
      width: "90%"
    },
    cell: {
      padding: theme.spacing(0.5),
      fontSize: "0.85em !important"
    },
    tableCell: {
      fontSize: "0.8rem !important",
      textAlign: "center"
    },
    cellParent: {
      flex: 1,
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      padding: theme.spacing(0, 0.5)
    },
    measureTable: {
      width: "fit-content",
      margin: "0px auto",
      marginTop: theme.spacing(1.5)
    },
    headerTitle: {
      fontWeight: "bold",
      cursor: "pointer"
    },
    calculateButton: {
      padding: theme.spacing(0),
      fontSize: "0.7rem"
    },
    recalculate: {
      display: "flex",
      flexDirection: "row-reverse",
      alignItems: "center"
    },
    recalculateButton: {
      padding: theme.spacing(0),
      marginRight: theme.spacing(1)
    },
  })
);

const ExportDialog = (props) => {
  const {open, classes, onClose} = props;

  const {viewer} = useViewer();
  const {t} = useTranslation();

  const [exportType, setExportType] = useState("csv");
  const [ordering, setOrdering] = useState("xyz");

  const onSubmit = () => {
    viewer?.exportMeasurements(exportType, ordering);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        {t('measure.measurements-export')}
      </DialogTitle>

      <DialogContent>
        {/* Export type */}
        <InputLabel>{t('measure.export-type')}</InputLabel>
        <Select
          fullWidth
          value={exportType}
          className={classes.textField}
          onChange={(event) => {
            setExportType(event.target.value as string);
          }}
        >
          <MenuItem value={"csv"}>{t('measure.csv-file')}</MenuItem>
          <MenuItem value={"dat"}>{t('measure.measure-data')}</MenuItem>
        </Select>

        {exportType === "csv" && (
          <React.Fragment>
            {/* Ordering */}
            <InputLabel>{t('measure.ordering')}</InputLabel>
            <Select
              fullWidth
              value={ordering}
              className={classes.textField}
              onChange={(event) => {
                setOrdering(event.target.value as string);
              }}
            >
              <MenuItem value={"xyz"}>{t('measure.easting-northing-elevation')}</MenuItem>
              <MenuItem value={"yxz"}>{t('measure.northing-easting-elevation')}</MenuItem>
            </Select>
          </React.Fragment>
        )}

        {exportType !== "csv" && (
          <Typography variant="caption">
            {t('measure.the-file-generated-using-measure-data')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('buttons.cancel')}
        </Button>

        <Button
          onClick={onSubmit}
          color="primary"
        >
          {t('buttons.export')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MeasureTypeIcon = (props) => {
  const {type, title, icon, measureType, setMeasureType} = props;

  const onClick = () => {
    setMeasureType(type);
  };

  return (
    <ToolBarButton
      title={title}
      icon={icon}
      onClick={onClick}
      color={measureType===type ? "secondary" : "primary"}
    />
  );
};

const ImportExportIcon = (props) => {
  const {icon, onClick} = props;
  const {t} = useTranslation();

  return (
    <ToolBarButton
      title={t('measure.import-export-measurements')}
      icon={icon}
      onClick={onClick}
      success
    />
  );
};

const ClipboardCopy = (props) => {
  const {text, ...other} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  return (
    <div
      className={clsx(classes.copyClipboard)}
      onClick={(event) => {
        event.stopPropagation();
        copyTextToClipboard(text);
        toast.success(t("toast.copied-to-clipboard"));
      }}
      {...other}
    >
      <ArrowTooltip title={t('measure.click-to-copy-to-clipboard')}>
        {props.children}
      </ArrowTooltip>
    </div>
  );
};

const MeasurementCard = (props) => {
  const {measurement, first} = props;

  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const renameDialog = useDialog();
  const deleteDialog = useDialog();

  const [expanded, setExpanded] = useState(false);
  const formatter = viewer.getMeasurementFormatter();

  const onMouseOver = () => {
    viewer?.updateMeasureHover(measurement, true);
  };

  const onMouseLeave = () => {
    viewer?.updateMeasureHover(measurement, false);
  };

  const generateVertexPositions = () => {
    if (!(measurement instanceof Measurement3D)) {
      return (<React.Fragment/>);
    }

    const precision = formatter.getVectorPrecision(LocalScene.viewProjection);
    const verticesReprojected = measurement.vertices.map(vertex => {
      return vertex.toViewProjection();
    });

    const singlePoint = verticesReprojected.length === 1;

    if (singlePoint) {
      const x = verticesReprojected[0].x.toFixed(precision.x);
      const y = verticesReprojected[0].y.toFixed(precision.y);
      const z = verticesReprojected[0].z.toFixed(precision.z);
      const text = `(${x}, ${y}, ${z})`;

      return (
        <div className={classes.row}>
          {/** Single point */}
          <div className={classes.cellParent}>
            <div className={clsx(classes.cell, classes.bold)}>
              {t('measure.position')}
            </div>

            <ClipboardCopy text={text} className={classes.cell}>
              <div>{text}</div>
            </ClipboardCopy>
          </div>
        </div>
      );
    }

    return (
      <TableContainer >
        <Table size="small" className={classes.measureTable}>

          <TableHead>
            <TableRow>
              <TableCell
                className={clsx(classes.bold)}
                align="center"
              >
                {t('measure.measurement_positions_xyz')}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {verticesReprojected.map((vertex, index) => {
              const x = vertex.x.toFixed(precision.x);
              const y = vertex.y.toFixed(precision.y);
              const z = vertex.z.toFixed(precision.z);
              const text = `(${x}, ${y}, ${z})`;

              return (
                <TableRow key={index}>
                  <TableCell>
                    <ClipboardCopy text={text} className={classes.tableCell}>
                      <div>{text}</div>
                    </ClipboardCopy>
                  </TableCell>
                </TableRow>
              );

            })}
          </TableBody>

        </Table>
      </TableContainer>
    );
  };

  const getVolumeComponent = (measurement: MeasurementVolume) => {
    if (!measurement.valid) {
      return measurement.invalidVolumeMessage;
    }

    if (measurement.calculated) {
      const value = formatter.formatVolume(measurement);

      return (
        <div className={classes.recalculate}>
          <div dangerouslySetInnerHTML={{__html: value}}/>
          <ArrowTooltip title={t('measure.recalculate-volume')}>
            <IconButton
              className={classes.recalculateButton}
              onClick={(event) => {
                event.stopPropagation();
                measurement.calculateVolume();
              }}
            >
              <RotateRightIcon fontSize="small"/>
            </IconButton>
          </ArrowTooltip>
        </div>
      );
    }

    return (
      <Button
        size="small"
        disabled
        className={classes.calculateButton}
      >
        {t('measure.calculating')}
      </Button>
    );
  };

  const handleRenameSubmit = name => {
    renameDialog.handleClose();
    viewer?.renameMeasurement(measurement, name);
  };

  const handleDeleteSubmit = () => {
    deleteDialog.handleClose();
    viewer?.deleteMeasurement(measurement);
  };

  const getVolumeMethod = () => {
    return (measurement.volumeType === VolumeType.LowestPlane)
      ? t('measure.volume_lowest_plane')
      : t('measure.volume_multi_plane');
  };

  const getVolumeRate = () => {
    if (measurement.sampleRate === SamplingRate.High) return t('general.high');
    if (measurement.sampleRate === SamplingRate.Medium) return t('general.medium');
    if (measurement.sampleRate === SamplingRate.Low) return t('general.low');
    return t('general.unknown');
  };

  useEffect(() => {
    setExpanded(first);
  }, [first]);

  const positions = generateVertexPositions();

  const is3DPoint = measurement instanceof MeasurementPoint;
  const is3DArea = measurement instanceof MeasurementArea;
  const isVolume = measurement instanceof MeasurementVolume;
  const is2DArea = measurement instanceof AerialAreaMeasurement;
  const is3DLength = measurement instanceof Measurement3D && !is3DPoint && !is3DArea;
  const is2DLength = measurement instanceof AerialLengthMeasurement;
  const hasHeight = measurement instanceof HeightMeasurement;
  const isStationMeasurement = measurement instanceof StationMeasurement;

  return (
    <React.Fragment>
      <Card
        className={classes.card}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
      >
        {/* Header */}
        <CardHeader
          className={classes.cardHeader}
          classes={{
            action: classes.cardButtons
          }}
          action={
            <React.Fragment>
              {/* Expand/collapse measurement */}
              <ArrowTooltip title={expanded? t('general.collapse') : t('general.expand')}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setExpanded(!expanded);
                  }}
                >
                  {expanded && <ExpandLessIcon fontSize="small"/>}
                  {!expanded && <ExpandMoreIcon fontSize="small"/>}
                </IconButton>
              </ArrowTooltip>

              {/* Rename measurement */}
              <ArrowTooltip title={t('measure.rename')}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    renameDialog.handleOpen();
                  }}
                >
                  <EditIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

              {/* Delete measurement */}
              <ArrowTooltip title={t('measure.delete')}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    deleteDialog.handleOpen();
                  }}
                >
                  <DeleteIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

            </React.Fragment>
          }
          title={measurement.title}
          titleTypographyProps={{
            variant: "body2",
            className: classes.headerTitle
          }}
          subheader={t('measure.total-vertices-measurement', {
            count: measurement.numVertices
          })}
          subheaderTypographyProps={{
            variant: "caption"
          }}
        />

        {/* Expandable content */}
        {expanded && (<React.Fragment>
          <CardContent className={classes.cardContent}>

            {is3DLength && (<React.Fragment>
              <div className={classes.row}>
                {/** 2D Distance */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.row-distance-2d')}
                  </div>

                  <div className={classes.cell}>
                    {formatter.formatLength(measurement.distance2D)}
                  </div>
                </div>

                {/** Grade */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.row-grade')}
                  </div>

                  <div className={classes.cell}>
                    {formatter.formatGrade(measurement.grade)}
                  </div>
                </div>
              </div>

              <div className={classes.row}>
                {/** 3D Distance */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.row-distance-3d')}
                  </div>

                  <div className={classes.cell}>
                    {formatter.formatLength(measurement.distance3D)}
                  </div>
                </div>

                {/** Angle */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.row-angle')}
                  </div>

                  <div className={classes.cell}>
                    {formatter.formatGrade(measurement.angle)}
                  </div>
                </div>
              </div>
            </React.Fragment>)}

            {hasHeight && (<React.Fragment>
              <div className={classes.row}>
                {/** Height */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.row-height')}
                  </div>

                  <div className={classes.cell}>
                    {formatter.formatLength(measurement.height)}
                  </div>
                </div>

                {/** Blank div */}
                <div className={classes.cellParent}/>
              </div>
            </React.Fragment>)}

            {is3DArea && (<React.Fragment>
              <div className={classes.row}>
                {/** Area */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.row-area')}
                  </div>

                  <div className={classes.cell} dangerouslySetInnerHTML={{
                    __html: measurement.valid
                      ? formatter.formatArea(measurement.area3D)
                      : measurement.areaInvalidMessage
                  }}/>
                </div>

                {/** Volume */}
                {isVolume && (<div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.row-volume')}
                  </div>

                  <div className={classes.cell}>
                    {getVolumeComponent(measurement)}
                  </div>
                </div>)}

                {/** Blank div */}
                {!isVolume && (<div className={classes.cellParent}/>)}
              </div>
            </React.Fragment>)}

            {is2DArea && (<React.Fragment>
              <div className={classes.row}>
                {/** Area */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {measurement.header}
                  </div>
                  <div className={classes.cell} dangerouslySetInnerHTML={{
                    __html: formatter.formatArea(measurement.area2d)
                  }}/>
                </div>

                {/** Blank div */}
                <div className={classes.cellParent}/>
              </div>
            </React.Fragment>)}

            {is2DLength && (<React.Fragment>
              <div className={classes.row}>
                {/** 2D Distance */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {measurement.header}
                  </div>
                  <div className={classes.cell}>
                    {formatter.formatLength(measurement.distance2D)}
                  </div>
                </div>

                {/** Blank div */}
                <div className={classes.cellParent}/>
              </div>
            </React.Fragment>)}

            {isVolume && (<React.Fragment>
              <div className={classes.row}>

                {/** Volume Type */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.method')}
                  </div>

                  <div className={classes.cell}>
                    {getVolumeMethod()}
                  </div>
                </div>

                {/** Sampling Rate */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.sampling_rate')}
                  </div>

                  <div className={classes.cell}>
                    {getVolumeRate()}
                  </div>
                </div>

              </div>
            </React.Fragment>)}

            {isStationMeasurement && (<React.Fragment>
              <div className={classes.row}>
                {/** Station*/}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.station-distAlong')}
                  </div>

                  <div className={classes.cell}>
                    {formatter.formatStation(measurement)}
                  </div>
                </div>

                {/** Offset */}
                <div className={classes.cellParent}>
                  <div className={clsx(classes.cell, classes.bold)}>
                    {t('measure.station-distAcross')}
                  </div>

                  <div className={classes.cell}>
                    {formatter.formatOffset(measurement)}
                  </div>
                </div>
              </div>
            </React.Fragment>)}

            {positions}

          </CardContent>
        </React.Fragment>)}
      </Card>

      {/* Delete Dialog */}
      <PromptDialog
        open={deleteDialog.open}
        onSubmit={handleDeleteSubmit}
        onCancel={deleteDialog.handleClose}
        title={t('measure.delete-entry')}
        prompt={t('measure.do-you-want-to-delete-this-measurement')}
        button={t('buttons.delete')}
      />

      {/* Rename Dialog */}
      <TextDialog
        open={renameDialog.open}
        onSubmit={handleRenameSubmit}
        onCancel={renameDialog.handleClose}
        title={t('measure.rename-measurement')}
        prompt={t('measure.enter-new-name')}
        placeholder={measurement.title}
        label={t('measure.measurement-name')}
      />
    </React.Fragment>
  );
};

export const Measurements = memo((props: any) => {
  const {measurementsDialog, measurements} = props;

  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const contextMenu = useContextMenu();
  const exportDialog = useDialog();
  const uniqueProjectID = useUniqueProjectID();
  const {setActiveToolOpen} = useActiveTool();

  const assets = useSelector(selectAllAssets);
  const xmlAssets = getLandXMLAssets(assets);

  const dataProjection = useSelector(selectDataProjection);
  const measurementUnits = useSelector(selectMeasureUnits);
  const [activeMeasurePrompt, setActiveMeasurePrompt] = useState(false);
  const [measureType, setMeasureType] = useState(MeasureType.Length);
  const [alignmentChoice, setAlignmentChoice] = useState("");
  const [measureUnits, setMeasureUnits] = useState('m');

  const loadImportData = (text) => {
    let encoded = decodeURIComponent(text);
    let data = JSON.parse(encoded);
    viewer.loadMeasurementData(data);
  };

  const openImportElectron = () => {
    dialog.showOpenDialog({
      filters: measureDataFilter,
      properties: ['openFile']
    }).then(result => {
      if (result.canceled) {
        return;
      }

      if (result.filePaths) {
        const filePath = slash(result.filePaths[0]);
        fs.readFile(filePath, 'utf-8', (err, text) => {
          if (err) {
            dialog.showErrorBox(t('dialog.measure-load-title'),
              t('dialog.measure-load-message'));
          }

          loadImportData(text);
        });
      }
    }).catch(() => {
      dialog.showErrorBox(t("dialog.general-error-title"),
        t("dialog.file-load-error"));
    });
  };

  const openImportBrowser = () => {
    fileDialog({accept: `.${measureDataFormat}`})
      .then(files => {
        if (files.length === 0) {
          return;
        }

        let reader = new FileReader();
        reader.onload = event => {
          let text = event.target.result;
          loadImportData(text);
        };
        reader.readAsText(files[0]);
      });
  };

  const onExport = () => {
    contextMenu.handleClose();
    exportDialog.handleOpen();
  };

  const onImport = () => {
    contextMenu.handleClose();

    if (isStaticSite) {
      openImportBrowser();
    } else {
      openImportElectron();
    }
  };

  const handleUnitsChange = (event) => {
    const value = event.target.value;
    dispatch(changeMeasureUnits(value));
  };

  const onModalClose = () => {
    setActiveMeasurePrompt(false);
  };

  const checkClose = () => {
    if (measurements.length > 0) {
      setActiveMeasurePrompt(true);
      return;
    }

    measurementsDialog.handleClose();
  };

  /** Update the default dropdown value if no alignment is selected */
  const updateDefaultDropdown = () => {
    const xmlAlignments = xmlAssets.map(asset => {
      const alignments = (asset.data as XMLData).alignments;
      return alignments.map(alignment => alignment.id);
    }).flat();

    if (xmlAlignments.includes(alignmentChoice)) return;
    setAlignmentChoice(xmlAlignments[0]);
  };

  useEffect(() => {
    /** Keep track if a tool is currently being used */
    setActiveToolOpen(measurementsDialog.open);
  }, [measurementsDialog.open]);

  useEffect(() => {
    /** Close dialog when new project is loaded */
    measurementsDialog.handleClose();
  }, [uniqueProjectID]);

  useEffect(() => {
    viewer?.toggleMeasurements(measurementsDialog.open);
    viewer?.updateMeasurementType(measureType);
  }, [viewer, measurementsDialog.open]);

  useEffect(() => {
    if (!viewer) return;
    viewer.updateMeasurementType(measureType);
  }, [viewer, measureType]);

  useEffect(() => {
    if (!measurementUnits) return;

    setMeasureUnits(measurementUnits);
    viewer?.setMeasurementUnits(measurementUnits);
  }, [viewer, measurementUnits]);

  useEffect(() => {
    measurementsDialog.handleClose();
  }, [dataProjection]);

  useEffect(() => {
    if (!viewer) return;
    viewer.setActiveAlignment(alignmentChoice);
  }, [viewer, alignmentChoice]);

  useEffect(() => {
    if (xmlAssets.length === 0) {
      setAlignmentChoice("");
      if (!isStationMeasurement) return;
      setMeasureType(MeasureType.Length);
    } else {
      updateDefaultDropdown();
    }
  }, [xmlAssets]);

  const reversed = [...measurements].reverse();
  const iconProps = {classes, measureType, setMeasureType};

  const exportDisabled = measurements.length === 0;
  const importDisabled = viewer?.hasCameras || viewer?.hasPoints;
  const isImageOnly = viewer?.isImageOnly;

  const isStationMeasurement = (measureType === MeasureType.Station);
  const allowStationMeasurements = xmlAssets.length > 0;

  return (
    <React.Fragment>
      <DraggableDialog
        initialHeight={500}
        open={measurementsDialog.open}
        title={t("measure.measurements")}
        onClose={checkClose}
      >
        {/* Measurement icons */}
        <IconToolBar>

          <MeasureTypeIcon
            type={MeasureType.Length}
            title={t('measure.standard')}
            icon="fa-ellipsis-h"
            {...iconProps}
          />

          <MeasureTypeIcon
            type={MeasureType.Point}
            title={t('measure.single-point')}
            icon="fa-circle-o"
            {...iconProps}
          />

          {(isImageOnly === false) && (<MeasureTypeIcon
            type={MeasureType.SnapUp}
            title={t('measure.snap-up')}
            icon="fa-arrow-up"
            {...iconProps}
          />)}

          {(isImageOnly === false) && (<MeasureTypeIcon
            type={MeasureType.SnapDown}
            title={t('measure.snap-down')}
            icon="fa-arrow-down"
            {...iconProps}
          />)}

          {(isImageOnly === false) && (<MeasureTypeIcon
            type={MeasureType.Height}
            title={t('measure.measure_height')}
            icon="fa-arrows-v"
            {...iconProps}
          />)}

          <MeasureTypeIcon
            type={MeasureType.Area}
            title={t('measure.area')}
            icon="fa-square-o"
            {...iconProps}
          />

          {(isImageOnly === false) && (<MeasureTypeIcon
            type={MeasureType.Volume}
            title={t('measure.volume')}
            icon="fa-codepen"
            {...iconProps}
          />)}

          {(allowStationMeasurements) && (<MeasureTypeIcon
            type={MeasureType.Station}
            title={t("measure.station-measure")}
            icon="fa-sort-amount-asc"
            {...iconProps}
          />)}

          <ToolBarDivider/>

          <ImportExportIcon
            onClick={contextMenu.handleOpen}
            icon="fa-download"
            {...iconProps}
          />

        </IconToolBar>

        {/* Alignment dropdown */}
        {isStationMeasurement && allowStationMeasurements && <React.Fragment >
          <InputLabel className={classes.alignmentSelect}>
            {t("measure.alignment")}
          </InputLabel>

          <Select
            fullWidth
            value={alignmentChoice}
            className={classes.alignmentSelect}
            onChange={(event) =>{
              let value = event.target.value as any;
              setAlignmentChoice(value);
            }}
          >
            {xmlAssets.map(asset => {
              const alignments = (asset.data as XMLData).alignments;

              return [
                <ListSubheader key={nanoid()} disableSticky>
                  {asset.name}
                </ListSubheader>,

                ...alignments.map(alignment =>
                  <MenuItem key={nanoid()} value={alignment.id}>
                    {alignment.name}
                  </MenuItem>
                )
              ];
            }).flat()}
          </Select>
        </React.Fragment>}

        <DenseDivider variant="middle" spacing={5}/>

        {/* Measurement list */}
        <SlimScrollbar>
          {measurements.length === 0 && (<Typography
            variant="caption"
            className={classes.userWarning}
          >
            {t('measure.no-measurements-have-been-added')}
          </Typography>)}

          <List dense>
            {reversed.map((measurement, index) =>
              <MeasurementCard
                key={measurement.id}
                first={index===0}
                measurement={measurement}
              />
            )}
          </List>
        </SlimScrollbar>

        {/* Measurement footer */}
        <div className={classes.footerDiv}>

          {/* Measurement units */}
          <div>
            <RadioGroup
              aria-label="anonymous"
              name="measure-units"
              value={measureUnits}
              onChange={handleUnitsChange}
              className={classes.measurementUnitsParent}
              row
            >
              <FormControlLabel
                value="m"
                className={classes.measurementUnits}
                control={<Radio size="small" classes={{root: classes.measurementUnitsRadio}}/>}
                label={<Typography variant="caption">{t('units.meters')}</Typography>}
              />

              <FormControlLabel
                value="ft"
                className={classes.measurementUnits}
                control={<Radio size="small" classes={{root: classes.measurementUnitsRadio}}/>}
                label={<Typography variant="caption">{t('units.feet')}</Typography>}
              />

              <FormControlLabel
                value="sft"
                className={classes.measurementUnits}
                control={<Radio size="small" classes={{root: classes.measurementUnitsRadio}}/>}
                label={<Typography variant="caption">{t('units.survey-feet')}</Typography>}
              />
            </RadioGroup>
          </div>

        </div>
      </DraggableDialog>

      {/* Import/Export menu */}
      <DenseMenu
        open={contextMenu.open}
        onClose={contextMenu.handleClose}
        anchorPosition={contextMenu.anchorPosition}
      >
        <MenuItem
          disabled={exportDisabled}
          onClick={onExport}
        >
          {t('measure.export-measurements')}
        </MenuItem>

        <MenuItem
          disabled={!importDisabled}
          onClick={onImport}
        >
          {t('measure.import-measurements')}
        </MenuItem>
      </DenseMenu>

      {/* Export Measurements */}
      <ExportDialog
        open={exportDialog.open}
        onClose={exportDialog.handleClose}
        classes={classes}
      />

      {/* Discard measurements dialog */}
      <PromptDialog
        open={activeMeasurePrompt}
        onCancel={onModalClose}
        onSubmit={() => {
          onModalClose();
          measurementsDialog.handleClose();
        }}
        title={t('asset.discard-measurements')}
        prompt={t('asset.active-measurements-found')}
        button={t('buttons.discard')}
      />

    </React.Fragment>
  );
});
